/**
 * @fileoverview Saved-card bodies for the Dashboard board (MOR-906 §7.8)
 *
 * A pinned widget and a pinned table each re-run their own saved spec, so
 * each owns its own subscription and its own loading state. The board
 * supplies the chrome (drag, resize, remove); these supply the contents.
 *
 * Headers are not draggable inside a pinned table — rearranging a table's
 * layout belongs to the builder, where the change can be saved.
 *
 * Used by:
 * - src/components/timesheets/FavouriteReportsBoard.tsx
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { format } from "date-fns";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TimePivotSpec } from "@cvx/timesheets/savedPivotReports/schema";

import { Spinner } from "@/ui/primitives/spinner";
import { formatDateRange } from "./format";
import { PivotTable } from "./PivotTable";
import { ReportChart, type ReportCategorySwatch } from "./ReportChart";
import type { WidgetSpec } from "./widgetSpec";

/** A saved widget row, as the board reads it. */
export interface FavouriteWidget {
  _id: Id<"savedTimeReports">;
  name: string;
  spec: WidgetSpec;
  isFavourite?: boolean;
  sortOrder?: number;
  size?: "half" | "full";
}

/** A saved pivot table row, as the board reads it. */
export interface FavouritePivotReport {
  _id: Id<"savedPivotReports">;
  name: string;
  spec: TimePivotSpec;
  isFavourite?: boolean;
  sortOrder?: number;
  size?: "half" | "full";
}

function Loading() {
  return (
    <div className="flex h-40 items-center justify-center">
      <Spinner className="text-muted-foreground" />
    </div>
  );
}

function Period({ start, end }: { start: string; end: string }) {
  return (
    <p className="mb-2 text-xs text-muted-foreground">
      {formatDateRange(start, end)}
    </p>
  );
}

export function WidgetCardBody({
  orgId,
  report,
  categories,
}: {
  orgId: Id<"organizations">;
  report: FavouriteWidget;
  categories?: ReportCategorySwatch[];
}) {
  const { data: result } = useQuery(
    convexQuery(api.timesheets.reportBuilder.runReport, {
      orgId,
      spec: report.spec,
      today: format(new Date(), "yyyy-MM-dd"),
    }),
  );

  if (result === undefined) return <Loading />;
  return (
    <>
      <Period start={result.startDate} end={result.endDate} />
      <ReportChart
        rows={result.rows}
        seriesNames={result.seriesNames}
        chart={report.spec.chart}
        metricLabel={result.metricLabel}
        groupLabel={report.spec.groupBy}
        groupBy={report.spec.groupBy}
        categories={categories}
      />
    </>
  );
}

export function PivotCardBody({
  orgId,
  report,
}: {
  orgId: Id<"organizations">;
  report: FavouritePivotReport;
}) {
  const { data: result } = useQuery(
    convexQuery(api.timesheets.pivot.runPivot, {
      orgId,
      spec: report.spec,
      today: format(new Date(), "yyyy-MM-dd"),
    }),
  );

  if (result === undefined) return <Loading />;
  return (
    <>
      <Period start={result.startDate} end={result.endDate} />
      <PivotTable result={result} title={report.name} />
    </>
  );
}
